import axios from '@/api/axios.config'
import { z } from 'zod'
import { pageSetSchema } from '@messenger/types'
import type {
  CreateTemplateWithPagesBody,
  GetFuneralHomeTemplateNormalized,
  ReplaceTemplatePagesBody,
  GetGlobalTemplateNormalized,
} from '@messenger/types'

/** The template and its pages a layout save answers with. */
type TemplateWithPages = {
  template: GetFuneralHomeTemplateNormalized | GetGlobalTemplateNormalized
  pages: Array<Record<string, unknown>>
}

/**
 * Update layout: replaces the template's pages with `body.pages` and writes
 * its fields in one request, which changes the whole template or nothing.
 */
export const replacePages = async (
  pageSetId: string,
  body: ReplaceTemplatePagesBody,
) => await axios.put<TemplateWithPages>(`pageSets/${pageSetId}/pages`, body)

/**
 * Save as layout: creates a funeral home template with its pages, in one
 * request, filed under the saving user's own funeral home; pages holding a
 * visible Lotus box, from an order of a funeral home the Lotus limit lists,
 * are filed under that order's funeral home.
 */
export const createWithPages = async (body: CreateTemplateWithPagesBody) =>
  await axios.post<TemplateWithPages>(`pageSets`, body)

export const archive = async (pagesetId: string) => {
  return await axios.patch<
    GetFuneralHomeTemplateNormalized | GetGlobalTemplateNormalized | null
  >(`archivePageSet/${pagesetId}`)
}

export const getCustomPageSets = async (
  productId: z.infer<typeof pageSetSchema>['productId'],
  themeId: z.infer<typeof pageSetSchema>['themeId'],
  orderId: z.infer<typeof pageSetSchema>['orderId'],
  applicationType: z.infer<typeof pageSetSchema>['applicationType'],
) => {
  const queryParams = new URLSearchParams()

  if (productId) {
    queryParams.append('product_id', productId)
  }

  if (themeId) {
    queryParams.append('theme_id', themeId)
  }

  if (orderId) {
    queryParams.append('order_id', orderId)
  }

  queryParams.append('custom_pages', 'true')

  if (applicationType) {
    queryParams.append('application_type', applicationType)
  }

  return await axios.get<
    GetFuneralHomeTemplateNormalized[] | GetGlobalTemplateNormalized[]
  >(`pageSets?${queryParams.toString()}`)
}
